import { ReactElement, useCallback, useEffect, useState } from 'react';
import { createSignalRContext } from 'react-signalr';
import { format } from 'date-fns';

import { useStoreActions, useStoreState } from '~/store/hooks';
import useRole from '~/store/user/hooks/useRole';
import formatClientAddress from '~/utils/dataFormat/formatClientAddress';

import { IDictionary } from '~/services/api/dictionaries/types';
import { IAddressExtended } from '~/services/api/types';
import Modal from '~/ui/components/common/Modal';
import Button from '~/ui/components/common/Button';

import AlertsOrderBy from '~/ui/pages/Notifications/models/alertsOrderBy';
import { SortDirection } from '~/ui/pages/Reports/constants/visitsSortOptions';
import { NOTIFICATIONS } from '~/ui/constants/paths';

import styles from './SignalRSOSAlert.module.scss';

export const SignalRContext = createSignalRContext({
  shareConnectionBetweenTab: true,
});

interface IAlertMessage {
  user: IDictionary;
  dateAndTime: string;
  address: IAddressExtended;
}

export const SignalRSOSAlert = (): ReactElement => {
  const [message, setMessage] = useState<IAlertMessage | null>(null);

  SignalRContext.useSignalREffect(
    'PanicRequestReceived',
    (...args: IAlertMessage[]) => {
      const [data] = args;

      if (data) {
        setMessage(data);
      }
    },
    [],
  );
  const user = useStoreState(state => state.user.current);
  const { isActTeam, isGlobalUser } = useRole();

  const { onGetSosRequests } = useStoreActions(actions => actions.sosRequests);

  // fetch alerts on message arrived
  const fetchAlerts = useCallback(async () => {
    if (user?.clinic?.id && !isActTeam && !isGlobalUser) {
      await onGetSosRequests({
        clinicId: String(user?.clinic?.id),
        params: {
          pageNumber: 1,
          pageSize: 25,
          onlyActive: true,
          sorting: AlertsOrderBy.DATE,
          sortDirection: SortDirection.Desc,
        },
      });
    }
  }, [user?.clinic?.id, isActTeam, isGlobalUser, onGetSosRequests]);

  const { pathname } = window?.location || {};
  const isNotificationsPage = pathname === NOTIFICATIONS;

  useEffect(() => {
    if (!isNotificationsPage && message) {
      fetchAlerts();
    }
  }, [isNotificationsPage, message, fetchAlerts]);

  const resetMessage = () => {
    setMessage(null);
  };

  if (!message) {
    return null;
  }

  return (
    <Modal open={!!message} className={styles.modal} onClose={resetMessage}>
      <div className={styles.modalWrapper}>
        <div className={styles.modalTitle}>Alert!</div>
        <p>
          {`${message.user.name} has clicked`}
          <br />
          <span>{`the SOS button${message.address ? ' from' : ''}:`}</span>
        </p>
        <p>{formatClientAddress(message.address)}</p>
        <p>{`at ${format(new Date(message.dateAndTime), "hh:mm aaa 'on' MM/dd/yyyy")}`}</p>
        <div className={styles.actionButtons}>
          <Button color="primary" variant="contained" onClick={resetMessage}>
            Ok
          </Button>
        </div>
      </div>
    </Modal>
  );
};
